import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PackageTable = ({ 
  packages, 
  onEdit, 
  onDuplicate, 
  onViewBookings, 
  onDelete, 
  onBulkAction,
  onStatusChange 
}) => {
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'lastModified', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'archived', label: 'Archived' }
  ];

  const bulkActionOptions = [
    { value: 'activate', label: 'Activate Selected' },
    { value: 'suspend', label: 'Suspend Selected' },
    { value: 'archive', label: 'Archive Selected' },
    { value: 'export', label: 'Export Selected' },
    { value: 'delete', label: 'Delete Selected' }
  ];

  const filteredAndSortedPackages = useMemo(() => {
    let filtered = packages.filter(pkg => {
      const matchesSearch = pkg.destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           pkg.title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || pkg.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'price') {
          aValue = parseFloat(aValue.replace(/[₹,]/g, ''));
          bValue = parseFloat(bValue.replace(/[₹,]/g, ''));
        } else if (sortConfig.key === 'lastModified') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [packages, searchTerm, statusFilter, sortConfig]);

  const paginatedPackages = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedPackages.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedPackages, currentPage]);

  const totalPages = Math.ceil(filteredAndSortedPackages.length / itemsPerPage);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPackages(paginatedPackages.map(pkg => pkg.id));
    } else {
      setSelectedPackages([]);
    }
  };

  const handleSelectPackage = (packageId, checked) => {
    if (checked) {
      setSelectedPackages(prev => [...prev, packageId]);
    } else {
      setSelectedPackages(prev => prev.filter(id => id !== packageId));
    }
  };

  const handleBulkAction = (action) => {
    if (selectedPackages.length > 0) {
      onBulkAction(action, selectedPackages);
      setSelectedPackages([]);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { bg: 'bg-muted', text: 'text-muted-foreground', icon: 'Edit' },
      active: { bg: 'bg-success', text: 'text-success-foreground', icon: 'CheckCircle' },
      suspended: { bg: 'bg-warning', text: 'text-warning-foreground', icon: 'Pause' },
      archived: { bg: 'bg-secondary', text: 'text-secondary-foreground', icon: 'Archive' }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon name={config.icon} size={12} />
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return 'ArrowUpDown';
    return sortConfig.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      {/* Table Header with Filters */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Input
              type="search"
              placeholder="Search packages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-80"
            />
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filter by status"
              className="w-full sm:w-48"
            />
          </div>

          {selectedPackages.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">
                {selectedPackages.length} selected
              </span>
              <Select
                options={bulkActionOptions}
                onChange={handleBulkAction}
                placeholder="Bulk actions"
                className="w-48"
              />
            </div>
          )}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-12 px-6 py-3 text-left">
                <Checkbox
                  checked={selectedPackages.length === paginatedPackages.length && paginatedPackages.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Package
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('destination')}>
                <div className="flex items-center space-x-1">
                  <span>Destination</span>
                  <Icon name={getSortIcon('destination')} size={14} />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('duration')}>
                <div className="flex items-center space-x-1">
                  <span>Duration</span>
                  <Icon name={getSortIcon('duration')} size={14} />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('price')}>
                <div className="flex items-center space-x-1">
                  <span>Price</span>
                  <Icon name={getSortIcon('price')} size={14} />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('status')}>
                <div className="flex items-center space-x-1">
                  <span>Status</span>
                  <Icon name={getSortIcon('status')} size={14} />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('bookings')}>
                <div className="flex items-center space-x-1">
                  <span>Bookings</span>
                  <Icon name={getSortIcon('bookings')} size={14} />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('lastModified')}>
                <div className="flex items-center space-x-1">
                  <span>Last Modified</span>
                  <Icon name={getSortIcon('lastModified')} size={14} />
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {paginatedPackages.map((pkg) => (
              <tr key={pkg.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4">
                  <Checkbox
                    checked={selectedPackages.includes(pkg.id)}
                    onChange={(e) => handleSelectPackage(pkg.id, e.target.checked)}
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted">
                      <Image
                        src={pkg.image}
                        alt={pkg.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{pkg.title}</div>
                      <div className="text-sm text-muted-foreground">{pkg.category}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-foreground">{pkg.destination}</td>
                <td className="px-6 py-4 text-sm text-foreground">{pkg.duration}</td>
                <td className="px-6 py-4 text-sm font-medium text-foreground">{pkg.price}</td>
                <td className="px-6 py-4">{getStatusBadge(pkg.status)}</td>
                <td className="px-6 py-4 text-sm text-foreground">{pkg.bookings}</td>
                <td className="px-6 py-4 text-sm text-muted-foreground">{pkg.lastModified}</td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(pkg)}
                      className="w-8 h-8"
                    >
                      <Icon name="Edit" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDuplicate(pkg)}
                      className="w-8 h-8"
                    >
                      <Icon name="Copy" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onViewBookings(pkg)}
                      className="w-8 h-8"
                    >
                      <Icon name="Users" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(pkg)}
                      className="w-8 h-8 text-destructive hover:text-destructive"
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {paginatedPackages.map((pkg) => (
          <div key={pkg.id} className="p-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                checked={selectedPackages.includes(pkg.id)}
                onChange={(e) => handleSelectPackage(pkg.id, e.target.checked)}
                className="mt-1"
              />
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                <Image
                  src={pkg.image}
                  alt={pkg.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-foreground truncate">{pkg.title}</h3>
                    <p className="text-sm text-muted-foreground">{pkg.destination}</p>
                  </div>
                  {getStatusBadge(pkg.status)}
                </div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{pkg.duration}</span>
                  <span className="font-medium text-foreground">{pkg.price}</span>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{pkg.bookings} bookings</span>
                  <span>{pkg.lastModified}</span>
                </div>
                <div className="mt-3 flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(pkg)}
                    iconName="Edit"
                    iconPosition="left"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewBookings(pkg)}
                    iconName="Users"
                    iconPosition="left"
                  >
                    Bookings
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedPackages.length)} of {filteredAndSortedPackages.length} packages
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Previous
              </Button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className="w-8 h-8"
                    >
                      {page}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageTable;